import { useEffect, useRef, useState } from "react";

/**
 * Drives a frame counter from 0 → durationMs at ~60fps using rAF.
 * Returns { frame, t, paused, toggle, replay } where t is 0..1.
 *
 * We deliberately use wall-clock time (not frame count) so the animation
 * stays at 20s even on low-fps devices; the `frame` value just gives scenes
 * a stable integer to range over.
 */
export const useFrame = (durationMs: number, playing: boolean) => {
  const [frame, setFrame] = useState(0);
  const [paused, setPaused] = useState(false);
  const startRef = useRef<number | null>(null);
  const pausedAtRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!playing || paused) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    const tick = (now: number) => {
      if (startRef.current == null) startRef.current = now - pausedAtRef.current;
      const elapsed = now - startRef.current;
      const clamped = Math.min(elapsed, durationMs);
      pausedAtRef.current = clamped;
      // 60fps reference frames (0..1200 over 20s)
      setFrame(Math.round((clamped / durationMs) * Math.round(durationMs / (1000 / 60))));
      if (clamped < durationMs) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, paused, durationMs]);

  const totalFrames = Math.round(durationMs / (1000 / 60));
  const t = Math.min(1, frame / totalFrames);

  const replay = () => {
    startRef.current = null;
    pausedAtRef.current = 0;
    setFrame(0);
    setPaused(false);
  };

  return {
    frame,
    totalFrames,
    t,
    paused,
    toggle: () => setPaused((p) => !p),
    replay,
    done: frame >= totalFrames,
  };
};

/** Linear interpolate, clamped to [0,1] on the input range. */
export const lerp = (frame: number, [a, b]: [number, number], [from, to]: [number, number]) => {
  if (frame <= a) return from;
  if (frame >= b) return to;
  const k = (frame - a) / (b - a);
  return from + (to - from) * k;
};

/** Ease-out cubic for natural motion. */
export const easeOut = (k: number) => 1 - Math.pow(1 - k, 3);

/** True while frame is within [start, end). */
export const between = (frame: number, start: number, end: number) =>
  frame >= start && frame < end;
